import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Consultation,
  ConsultationCreate,
  ConsultationListItem,
  Recording,
  ExtendedFormData,
} from '../models';
import {
  consultationService,
  recordingService,
  ConsultationFilters,
} from '../services/consultationService';

export const useConsultationListViewModel = (filters?: ConsultationFilters) => {
  const queryClient = useQueryClient();

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['consultations', filters],
    queryFn: () => consultationService.getList(filters),
  });

  const deleteMutation = useMutation({
    mutationFn: consultationService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consultations'] });
    },
  });

  return {
    consultations: data?.results ?? [],
    totalCount: data?.count ?? 0,
    totalPages: data?.total_pages ?? 0,
    currentPage: data?.current_page ?? 1,
    isLoading,
    error,
    refetch,
    deleteConsultation: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
  };
};

export const useConsultationDetailViewModel = (id: number) => {
  const queryClient = useQueryClient();

  const {
    data: consultation,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['consultation', id],
    queryFn: () => consultationService.getById(id),
    enabled: !!id,
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<Consultation>) => consultationService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consultation', id] });
      queryClient.invalidateQueries({ queryKey: ['consultations'] });
    },
  });

  const updateFormDataMutation = useMutation({
    mutationFn: ({ formData, templateId }: { formData: ExtendedFormData | Record<string, unknown>; templateId?: number | null }) =>
      consultationService.updateFormData(id, formData, templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consultation', id] });
    },
  });

  const updateSummaryMutation = useMutation({
    mutationFn: (summary: string) => consultationService.updateSummary(id, summary),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consultation', id] });
    },
  });

  return {
    consultation,
    isLoading,
    error,
    refetch,
    updateConsultation: updateMutation.mutate,
    isUpdating: updateMutation.isPending,
    updateFormData: updateFormDataMutation.mutate,
    isUpdatingFormData: updateFormDataMutation.isPending,
    updateSummary: updateSummaryMutation.mutate,
    isUpdatingSummary: updateSummaryMutation.isPending,
  };
};

export const useConsultationCreateViewModel = () => {
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: consultationService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consultations'] });
    },
  });

  return {
    createConsultation: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    error: createMutation.error,
  };
};

export const useRecordingViewModel = (consultationId: number) => {
  const queryClient = useQueryClient();
  const [uploadProgress, setUploadProgress] = useState(0);

  const {
    data: recordings,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['recordings', consultationId],
    queryFn: () => recordingService.getList(consultationId),
    enabled: !!consultationId,
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => recordingService.upload(consultationId, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recordings', consultationId] });
      queryClient.invalidateQueries({ queryKey: ['consultation', consultationId] });
      setUploadProgress(0);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: recordingService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recordings', consultationId] });
    },
  });

  const checkSTTStatus = useCallback(
    async (recordingId: number) => {
      const result = await recordingService.getSTTStatus(recordingId);
      // 상태 변화가 있으면 항상 새로고침 (completed, failed, 또는 doc_id 획득 시)
      if (result.status === 'completed' || result.status === 'failed' || result.wedms_doc_id) {
        queryClient.invalidateQueries({ queryKey: ['recordings', consultationId] });
        queryClient.invalidateQueries({ queryKey: ['consultation', consultationId] });
      }
      return result;
    },
    [queryClient, consultationId]
  );

  return {
    recordings: recordings ?? [],
    isLoading,
    error,
    refetch,
    uploadRecording: uploadMutation.mutate,
    isUploading: uploadMutation.isPending,
    uploadProgress,
    deleteRecording: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
    checkSTTStatus,
  };
};
